import { useEffect, useRef } from "react";
import { Form, InputNumber, Modal, Select } from "antd";
import type { FormInstance } from "antd";
import { useSelect } from "@refinedev/antd";
import { ICurrency } from "interfaces";
import type { Rule } from "antd/es/form";
import {
  OtherServiceDef,
  fmtAvcsFlatFee,
  isFlatFeeType,
  isPayPerUseType,
} from "components/metadata/otherServiceCatalog";

export const ERROR_AVCS_NOT_FLAT_FEE =
  "AVCS Service for this subscription is not configured as Flat Fee. Configure AVCS as a Flat Fee service first, or set this service to Pay Per Use.";

export const buildAvcsFlatFeeRules = ({
  avcsAmount,
  avcsSubscriptionType,
  enabled,
}: {
  avcsAmount: number | null;
  avcsSubscriptionType: string | null;
  enabled: boolean;
}): Rule[] => {
  if (!enabled) return [];
  return [
    {
      validator: (_rule, value) => {
        const flatFeeSelected = isFlatFeeType(value);
        const avcsValid =
          isFlatFeeType(avcsSubscriptionType ?? undefined) && (avcsAmount ?? 0) > 0;
        if (flatFeeSelected && !avcsValid) {
          return Promise.reject(new Error(ERROR_AVCS_NOT_FLAT_FEE));
        }
        return Promise.resolve();
      },
    },
  ];
};

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

interface PricingFieldsProps {
  form?: FormInstance;
  svc?: OtherServiceDef;
  prefix?: string;
  avcsFlatFeeAmount?: number | null;
  avcsFlatFeeCurrency?: string | null;
  avcsSubscriptionType?: string | null;
  lockCurrency?: boolean;
  errorMode?: "warn" | "block";
}

export const PricingFields: React.FC<PricingFieldsProps> = ({
  form,
  svc,
  prefix: prefixProp = "",
  avcsFlatFeeAmount = null,
  avcsFlatFeeCurrency = null,
  avcsSubscriptionType = null,
  lockCurrency = false,
  errorMode = "warn",
}) => {
  const { selectProps: currencySelectProps } = useSelect<ICurrency>({
    resource: "currency",
    optionLabel: "code",
    optionValue: "code",
    pagination: { mode: "server" },
    queryOptions: { enabled: lockCurrency },
  });

  const prefix = svc?.prefix ?? prefixProp;
  const usesPayPerUseAmount = !!svc?.usesPayPerUseAmount;

  const field = (suffix: string): string =>
    prefix ? `${prefix}${cap(suffix)}` : suffix;

  const subscriptionTypeField = field("subscriptionType");
  const ppuField = usesPayPerUseAmount
    ? field("payPerUseAmount")
    : field("discountPercentage");
  const ppuLabel = usesPayPerUseAmount
    ? "Pay Per Use Amount"
    : "Discount Percentage";
  const flatFeeField = field("annualFlatFeePerVessel");

  const currentType = Form.useWatch(subscriptionTypeField, form);
  const committedRef = useRef<string | undefined>(currentType);
  const revertingRef = useRef<boolean>(false);
  const modalOpenRef = useRef<boolean>(false);

  useEffect(() => {
    if (!form) return;
    if (revertingRef.current) {
      revertingRef.current = false;
      return;
    }
    if (currentType === committedRef.current) return;
    if (committedRef.current === undefined) {
      committedRef.current = currentType;
      return;
    }

    if (currentType === undefined) return;

    const nextType = currentType;
    const prevType = committedRef.current;

    const willHidePpu = isFlatFeeType(nextType);
    const willHideFlatFee = isPayPerUseType(nextType);
    const willHideFieldName = willHidePpu
      ? ppuField
      : willHideFlatFee
        ? flatFeeField
        : null;

    if (!willHideFieldName) {
      committedRef.current = nextType;
      return;
    }

    const currentValue = form.getFieldValue(willHideFieldName);
    const isEmpty =
      currentValue === 0 ||
      currentValue === null ||
      currentValue === undefined ||
      currentValue === "";

    if (isEmpty) {
      committedRef.current = nextType;
      return;
    }

    const fieldLabel = willHidePpu ? ppuLabel : "Annual Flat Fee Per Vessel";

    if (modalOpenRef.current) return;
    modalOpenRef.current = true;

    Modal.confirm({
      title: "Confirm subscription type change",
      content: `Switching to "${nextType}" will clear the current ${fieldLabel} value (${currentValue}). Continue?`,
      okText: "Continue",
      cancelText: "Cancel",
      onOk: () => {
        modalOpenRef.current = false;
        form.setFieldValue(willHideFieldName, 0);
        committedRef.current = nextType;
      },
      onCancel: () => {
        modalOpenRef.current = false;
        revertingRef.current = true;
        form.setFieldValue(subscriptionTypeField, prevType);
      },
    });
  }, [currentType, form, subscriptionTypeField, ppuField, ppuLabel, flatFeeField]);

  const showPpu = isPayPerUseType(currentType);
  const showFlatFee = isFlatFeeType(currentType);
  const hasValidAvcs =
    (avcsFlatFeeAmount ?? 0) > 0 &&
    (avcsSubscriptionType == null || isFlatFeeType(avcsSubscriptionType ?? undefined));
  const lockFlatFee =
    (usesPayPerUseAmount || lockCurrency) && showFlatFee && hasValidAvcs;
  const isInvalidAvcsForFlatFee = showFlatFee && !hasValidAvcs;
  const warnFlatFee =
    isInvalidAvcsForFlatFee && errorMode === "warn" && usesPayPerUseAmount;
  const errorFlatFee = isInvalidAvcsForFlatFee && errorMode === "block";
  const avcsHelper = fmtAvcsFlatFee(avcsFlatFeeAmount, avcsFlatFeeCurrency);
  const flatFeeHelp = errorFlatFee
    ? ERROR_AVCS_NOT_FLAT_FEE
    : warnFlatFee
      ? "AVCS Flat Fee is not configured (or is 0) for the linked subscription. Configure it on the AVCS Service subscription first."
      : showFlatFee && (usesPayPerUseAmount || lockCurrency)
        ? avcsHelper
        : undefined;

  useEffect(() => {
    if (!form) return;
    if (!lockFlatFee) return;
    if (avcsFlatFeeAmount == null) return;
    if (form.getFieldValue(flatFeeField) === avcsFlatFeeAmount) return;
    form.setFieldValue(flatFeeField, avcsFlatFeeAmount);
  }, [form, lockFlatFee, flatFeeField, avcsFlatFeeAmount]);

  const currencyField = field("currency");
  const shouldLockCurrency = lockFlatFee && lockCurrency;

  useEffect(() => {
    if (!form) return;
    if (!shouldLockCurrency) return;
    if (avcsFlatFeeCurrency == null) return;
    if (form.getFieldValue(currencyField) === avcsFlatFeeCurrency) return;
    form.setFieldValue(currencyField, avcsFlatFeeCurrency);
  }, [form, shouldLockCurrency, currencyField, avcsFlatFeeCurrency]);

  useEffect(() => {
    if (!form) return;
    if (errorMode !== "block") return;
    if (form.getFieldValue(subscriptionTypeField) == null) return;
    form.validateFields([subscriptionTypeField]).catch(() => {
      // expected to reject in invalid state — purpose is to surface the error UI
    });
  }, [
    form,
    errorMode,
    subscriptionTypeField,
    avcsFlatFeeAmount,
    avcsSubscriptionType,
    currentType,
  ]);

  return (
    <>
      {lockCurrency && (
        <Form.Item
          label="Currency"
          name={currencyField}
          rules={[{ required: true }]}
        >
          <Select {...currencySelectProps} disabled={shouldLockCurrency} />
        </Form.Item>
      )}
      {usesPayPerUseAmount ? (
        <Form.Item
          label={ppuLabel}
          name={ppuField}
          rules={
            showPpu
              ? [
                  {
                    type: "number",
                    min: 0,
                    max: 2500000,
                  },
                ]
              : []
          }
          hidden={!showPpu}
        >
          <InputNumber
            min={0}
            max={2500000}
            precision={2}
            style={{ width: "100%" }}
          />
        </Form.Item>
      ) : (
        <Form.Item
          label={ppuLabel}
          name={ppuField}
          rules={[{ required: false }]}
          hidden={!showPpu}
        >
          <InputNumber<number>
            min={0}
            max={100}
            precision={2}
            style={{ width: "100%" }}
            formatter={(value) =>
              value === undefined || value === null ? "" : `${value}%`
            }
            parser={(value) => {
              if (!value) return 0;
              const n = Number(value.replace(/%/g, ""));
              return Number.isFinite(n) ? n : 0;
            }}
          />
        </Form.Item>
      )}
      <Form.Item
        label="Annual Flat Fee Per Vessel"
        name={flatFeeField}
        rules={
          showFlatFee
            ? [{ required: true, type: "number", min: 0, max: 2500000 }]
            : []
        }
        hidden={!showFlatFee}
        help={flatFeeHelp}
        validateStatus={
          errorFlatFee ? "error" : warnFlatFee ? "warning" : undefined
        }
      >
        <InputNumber
          min={0}
          max={2500000}
          precision={2}
          disabled={lockFlatFee}
          style={{ width: "100%" }}
        />
      </Form.Item>
    </>
  );
};

export default PricingFields;
